import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { capitalize } from 'lodash';
import { GeneralUtilsService } from './common/generalUtils.service';
import { RangeOutput } from '../_models/common/RangeOutput';

export const ENGLISH_LOCALE = 'en';

@Injectable()
export class ChartsService {
  constructor(
    private generalUtilsService: GeneralUtilsService,
    private translate: TranslateService
  ) {
    this.setLanguage();
  }

  getDatetimeAxis(range: RangeOutput): Highcharts.XAxisOptions[] {
    const momentStart = moment(range.start);
    const momentEnd = moment(range.end);

    const timeSpan = momentStart.diff(momentEnd, 'day');
    const tickInterval = this.getTicketIntervalByTimespan(timeSpan);

    return [
      {
        type: 'datetime',
        tickInterval,
        opposite: true,
        tickColor: '#548f60',
        gridLineWidth: 1,
        gridLineColor: '#548f60',
        gridZIndex: 1,
        lineWidth: 2,
        lineColor: '#548f60',
        className: 'upper-axis',
      } as Highcharts.XAxisOptions,
    ];
  }

  getDatetimeAxisMonitoring(): Highcharts.XAxisOptions[] {
    return [
      {
        type: 'datetime',
        opposite: true,
        tickColor: '#548f60',
        gridLineWidth: 1,
        gridLineColor: '#548f60',
        gridZIndex: 1,
        lineWidth: 2,
        lineColor: '#548f60',
        className: 'upper-axis',
      },
    ];
  }

  private getTicketIntervalByTimespan(timespan: number): number {
    timespan = Math.abs(timespan);
    if (timespan < 7) return 21600000; // 6 hours
    if (timespan < 25) return 86400000; // one day
    if (timespan < 50) return 86400000 * 3; // three days
    return 86400000 * 7; // one week
  }

  setLanguage(lang?: string) {
    if (!lang) {
      lang = this.generalUtilsService.getLanguage();
    }

    moment.locale(lang);
    const options: Highcharts.Options = {
      lang: {
        shortMonths: moment.monthsShort().map((m) => capitalize(m)),
        months: moment.months().map((m) => capitalize(m)),
        weekdays: moment.weekdays().map((d) => capitalize(d)),
        noData: this.translate.instant('settingslist.label_text_nodata'),
      },
    };

    Highcharts.setOptions(options);
  }

  /**
   * Generates a human-readable date range label from an array of timestamp strings.
   *
   * If the timestamps array contains valid values, it returns a formatted string like:
   * "01 January - 31 March 2023". If both dates are in the same year, the starting year is omitted.
   * Translates month names using the translation service.
   *
   * @param {string[]} timestamps - An array of ISO timestamp strings.
   * @returns {string} A formatted date range string, or an empty string if the input is empty.
   */
  getDateRangeLabel(timestamps: string[]) {
    // Determine the number of timestamps
    const datasetSize = timestamps.length;

    // Get the first and last timestamps if available
    const startDate = datasetSize ? timestamps[0] : null;
    const endDate = datasetSize ? timestamps[datasetSize - 1] : null;

    // Proceed only if both start and end dates exist
    if (startDate && endDate) {
      // Create moment objects with English locale
      const _start = moment(startDate).locale(ENGLISH_LOCALE);
      const _end = moment(endDate).locale(ENGLISH_LOCALE);

      // Format day parts
      const startDay = _start.format('DD');
      const endDay = _end.format('DD');

      // Translate month names
      const startMonth = this.translate.instant('months.' + _start.format('MMMM').toLowerCase());
      const endMonth = this.translate.instant('months.' + _end.format('MMMM').toLowerCase());

      // Get year parts
      let startYear = _start.format('YYYY');
      const endYear = _end.format('YYYY');

      // If both dates are in the same year, omit the start year
      if (startYear == endYear) {
        startYear = '';
      }

      // Return formatted range string
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }

    // Return empty string if dates are invalid
    return '';
  }

  getTimeLabels(timestamps: string[]) {
    return timestamps.map((time) => moment(time).format('HH:mm'));
  }

  getWeekdaysLabels(timestamps: string[]) {
    return timestamps
      .map((time) => moment(time).locale(ENGLISH_LOCALE).format('dddd').toLowerCase())
      .map((weekday) => this.translate.instant('week.label_' + weekday));
  }
}
